import { Wallet } from "./wallet";
import { Eth } from "@/lib/web3x-es/eth";
import { Address } from "@/lib/web3x-es/address";
import { toBN } from "@/lib/web3x-es/utils";

import EventBus, { ACTIONS } from "@/event-bus";

declare let window: any;
let ethereum: any = null;

if (window.ethereum) {
  ethereum = window.ethereum;
} else {
  window.addEventListener(
    "ethereum#initialized",
    () => {
      ethereum = window.ethereum;
    },
    {
      once: true,
    }
  );

  // If the event is not dispatched by the end of the timeout,
  // the user probably doesn't have MetaMask installed.
  setTimeout(() => {
    ethereum = window.ethereum;
  }, 3000); // 3 seconds
}

class WalletExtension implements Wallet {
  private currentAddress = "";
  private currentChainId = "";
  private provider: any = "";
  private logoName = "";
  private isInstalledCondition = "";
  private chainIds: any = "";

  public constructor() {
    if (this.usingCoin98()) {
      this.logoName = "coin98";
      this.isInstalledCondition = "isCoin98";
      this.chainIds = {
        "0x1": "mainnet",
        "0x89": "matic",
        "0x38": "bsc",
      };
    } else if (this.usingMetamask()) {
      this.logoName = "metamask";
      this.isInstalledCondition = "isMetaMask";
      this.chainIds = {
        "0x1": "mainnet",
        "0x3": "ropsten",
        "0x4": "rinkeby",
        "0x5": "goerli",
        "0x2a": "kovan",
        "0x89": "matic",
        "0x13881": "mumbai",
        "0x38": "bsc",
      };
    }
  }

  public usingCoin98(): boolean {
    return Boolean(ethereum.isCoin98 || window.coin98);
  }

  public usingMetamask(): boolean {
    return Boolean(ethereum.isMetaMask);
  }

  public isInstalled() {
    return Boolean(ethereum && ethereum[this.isInstalledCondition]);
  }

  public isConnected(): boolean {
    return ethereum.isConnected();
  }

  public disconnect() {
    this.currentAddress = "";
    this.currentChainId = "";
    this.provider = null;
  }

  public getAddress() {
    return this.currentAddress;
  }

  public getChain() {
    return this.chainIds[this.currentChainId];
  }

  public getChainId() {
    return this.currentChainId;
  }

  public async connect() {
    try {
      const accounts = await ethereum.request({
        method: "eth_requestAccounts",
      });

      console.debug("connect OK... ->", accounts[0]);

      // set provider
      this.provider = new Eth(ethereum);

      this.currentAddress = accounts[0];
      this.currentChainId = await ethereum.request({
        method: "eth_chainId",
      });

      ethereum.on("chainChanged", (networkId: string) => {
        console.debug("chainChanged :: ", networkId);

        this.currentChainId = ethereum.chainId;
        EventBus.$emit(ACTIONS.ETH_UPDATE_ACCOUNTS);
      });

      ethereum.on("accountsChanged", (acc: string[]) => {
        console.debug("accountsChanged :: ", acc[0]);

        this.currentAddress = acc[0];
        EventBus.$emit(ACTIONS.ETH_UPDATE_ACCOUNTS);
      });

      ethereum.on("connect", () => {
        if (!this.isConnected()) {
          this.disconnect();
        }
        EventBus.$emit(ACTIONS.ETH_UPDATE_ACCOUNTS);
      });

      EventBus.$emit(ACTIONS.ETH_UPDATE_ACCOUNTS);
    } catch (err) {
      console.log(err);
    }
  }

  public async sendTransaction(txParams: any) {
    if (txParams.from === undefined) {
      txParams.from = this.getAddress();
    }

    if (txParams.value) {
      txParams.value = toBN(txParams.value).toString(16);
    }

    console.debug("txParams ::", txParams);

    return ethereum.request({
      method: "eth_sendTransaction",
      params: [txParams],
    });
  }

  public async getBlockNo(): Promise<number> {
    return this.provider.getBlockNumber();
  }

  public getBalance() {
    return this.provider.getBalance(Address.fromString(this.currentAddress));
  }

  public getEthProvider(): any {
    return this.provider;
  }

  public getIcon(): any {
    return `/img/logos/${this.logoName}.svg`;
  }
}

export default WalletExtension;
